import { createContext, useContext, useEffect, useRef, type PropsWithChildren } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/components/AuthProvider";
import { ApiError } from "@/lib/api";
import { createCoalescedAction } from "@/lib/coalescedAction";
import { fetchLaborOpenSession, type LaborActiveSessionPayload } from "@/lib/labor/laborApi";
import { subscribeLaborOpenSessionSync } from "@/lib/labor/laborOpenSessionSync";
import {
  laborPresenceEventClearsOpenSession,
  laborPresenceSessionFromStartedEvent,
} from "@/lib/labor/laborRemoteSessionEndSync";
import { openSessionFromLaborPayload, parseLaborSessionRow } from "@/lib/labor/parseLaborSession";
import type { LaborSession } from "@/lib/labor/laborApi";
import { isHubLaborPresenceEvent, useHubLaborPresenceStream } from "@/lib/useHubLaborPresenceStream";

export const LABOR_OPEN_SESSION_QUERY_KEY = ["hub", "labor", "open-session"] as const;

/** Cap open-session refetch storms from SSE + cross-tab sync during congestion. */
export const LABOR_OPEN_SESSION_REFETCH_MIN_INTERVAL_MS = 2_000;

export interface LaborActiveSessionContextValue {
  openPayload: LaborActiveSessionPayload | undefined;
  openSession: LaborSession | null;
  isLoading: boolean;
  isFetched: boolean;
  refetch: () => void;
}

const LaborActiveSessionContext = createContext<LaborActiveSessionContextValue | null>(null);

function applyLaborPresenceEventToOpenSessionCache(
  queryClient: ReturnType<typeof useQueryClient>,
  event: Parameters<typeof laborPresenceSessionFromStartedEvent>[0],
): void {
  const started = laborPresenceSessionFromStartedEvent(event);
  if (started != null) {
    queryClient.setQueryData<LaborActiveSessionPayload>([...LABOR_OPEN_SESSION_QUERY_KEY], (current) => {
      if (current === undefined) {
        return { session: started };
      }
      return {
        ...current,
        session: started,
        active_on_other_page: undefined,
        active_on_other_source: undefined,
      };
    });
    return;
  }
  const current = queryClient.getQueryData<LaborActiveSessionPayload>([...LABOR_OPEN_SESSION_QUERY_KEY]);
  const openRow = parseLaborSessionRow(current?.session ?? null);
  if (!laborPresenceEventClearsOpenSession(event, openRow?.id ?? null)) {
    return;
  }
  queryClient.setQueryData<LaborActiveSessionPayload>([...LABOR_OPEN_SESSION_QUERY_KEY], (existing) => {
    if (existing === undefined) {
      return existing;
    }
    return { ...existing, session: null };
  });
}

export function LaborActiveSessionProvider({ children }: PropsWithChildren) {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const query = useQuery<LaborActiveSessionPayload, ApiError>({
    queryKey: [...LABOR_OPEN_SESSION_QUERY_KEY],
    queryFn: ({ signal }) => fetchLaborOpenSession({ signal }),
    enabled: Boolean(session?.user),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const coalescedRefetchRef = useRef<ReturnType<typeof createCoalescedAction> | null>(null);
  if (coalescedRefetchRef.current == null) {
    coalescedRefetchRef.current = createCoalescedAction({
      minIntervalMs: LABOR_OPEN_SESSION_REFETCH_MIN_INTERVAL_MS,
      run: () =>
        queryClient.refetchQueries({
          queryKey: [...LABOR_OPEN_SESSION_QUERY_KEY],
        }, {
          cancelRefetch: false,
        }),
    });
  }

  const refetchOpenSession = (): void => {
    coalescedRefetchRef.current?.trigger();
  };

  useHubLaborPresenceStream({
    enabled: Boolean(session?.user),
    onPresenceChange: (event) => {
      if (!isHubLaborPresenceEvent(event)) {
        return;
      }
      // Instant lockdown / release from SSE payload; refetch confirms full row.
      applyLaborPresenceEventToOpenSessionCache(queryClient, event);
      refetchOpenSession();
    },
  });

  useEffect(() => {
    return subscribeLaborOpenSessionSync(() => {
      refetchOpenSession();
    });
  }, [queryClient]);

  useEffect(() => {
    return () => {
      coalescedRefetchRef.current?.dispose();
      coalescedRefetchRef.current = null;
    };
  }, []);

  const value: LaborActiveSessionContextValue = {
    openPayload: query.data,
    openSession: openSessionFromLaborPayload(query.data),
    isLoading: query.isLoading,
    isFetched: query.isFetched,
    refetch: refetchOpenSession,
  };

  return <LaborActiveSessionContext.Provider value={value}>{children}</LaborActiveSessionContext.Provider>;
}

const EMPTY_LABOR_ACTIVE_SESSION_CONTEXT: LaborActiveSessionContextValue = {
  openPayload: undefined,
  openSession: null,
  isLoading: false,
  isFetched: true,
  refetch: () => {},
};

export function useOptionalLaborActiveSessionContext(): LaborActiveSessionContextValue | null {
  return useContext(LaborActiveSessionContext);
}

/** Header surfaces (UserMenu) may mount before the labor shell is ready during dev HMR. */
export function useLaborActiveSessionContext(): LaborActiveSessionContextValue {
  const ctx = useOptionalLaborActiveSessionContext();
  if (ctx === null) {
    throw new Error("useLaborActiveSessionContext must be used within LaborActiveSessionProvider.");
  }
  return ctx;
}

export function useLaborActiveSessionContextForHubChrome(): LaborActiveSessionContextValue {
  return useOptionalLaborActiveSessionContext() ?? EMPTY_LABOR_ACTIVE_SESSION_CONTEXT;
}
